import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox';
import { LOADING_PLACEHOLDER } from '@/constants/app.constants';
import type { reportingAuthoritiesQueryOptions } from '@/features/users/api/users.api';
import {
  COMBOBOX_LIST_CLASS,
  COMBOBOX_PANEL_CLASS,
  DROPDOWN_ITEM_CLASS,
} from '@/styles/floatingPanel';
import { isSameComboboxOption } from '@/utils/comboboxOption';
import { getPersonOptionDetail } from '@/utils/personOption';
import { ComboboxSearchField } from './ComboboxSearchField';

interface MemberOption {
  label: string;
  value: number;
  hrEmpId: string;
  username: string;
  mobile: string;
}

interface MemberSelectProps {
  membersQuery: ReturnType<typeof reportingAuthoritiesQueryOptions>;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
}

function matchesMember(item: MemberOption, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;

  return [item.label, item.hrEmpId, item.username, item.mobile].some((field) =>
    field.toLowerCase().includes(needle),
  );
}

export function MemberSelect({
  membersQuery,
  value,
  onChange,
  placeholder = 'Pick a member',
  id,
  disabled,
}: MemberSelectProps) {
  const { data, isLoading } = useQuery(membersQuery);

  const items = useMemo<MemberOption[]>(
    () =>
      (data ?? []).map((member) => ({
        label: member.fullName,
        value: member.id,
        hrEmpId: member.hrEmpId,
        username: member.username,
        mobile: member.mobile,
      })),
    [data],
  );

  const selected = items.some((item) => item.value === value) ? (value ?? null) : null;

  return (
    <Combobox<number>
      items={items}
      value={selected}
      onValueChange={(next) => onChange(next ?? undefined)}
      isItemEqualToValue={isSameComboboxOption}
      filter={(item, query) => matchesMember(item as unknown as MemberOption, query)}
      disabled={disabled ?? isLoading}
    >
      <div className="relative">
        <ComboboxTrigger id={id}>
          <ComboboxValue placeholder={isLoading ? LOADING_PLACEHOLDER : placeholder} />
          <ComboboxIcon className={selected === null ? undefined : 'invisible'} />
        </ComboboxTrigger>
        {selected === null ? null : (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear the member"
            onClick={() => onChange(undefined)}
            className="absolute top-1/2 right-3 flex size-4 -translate-y-1/2 cursor-pointer items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <X className="size-3" />
          </button>
        )}
      </div>

      <ComboboxContent className={COMBOBOX_PANEL_CLASS}>
        <ComboboxSearchField placeholder="Name, HR id, login or mobile" />
        <ComboboxList className={COMBOBOX_LIST_CLASS}>
          {(item: MemberOption) => (
            <ComboboxItem key={item.value} value={item.value} className={DROPDOWN_ITEM_CLASS}>
              <span className="flex flex-col">
                <span>{item.label}</span>
                <span className="text-xs text-muted-foreground">{getPersonOptionDetail(item)}</span>
              </span>
            </ComboboxItem>
          )}
        </ComboboxList>
        <ComboboxEmpty>Nobody matches that.</ComboboxEmpty>
      </ComboboxContent>
    </Combobox>
  );
}
